import { useState } from "react";
import { Box, Card, CardContent, Divider, IconButton, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Lead } from "../constants";
import { STATUS_META, formatCurrency, getQualifiedMeta } from "../constants";
import { formatAcquiredDate, formatAcquiredTime } from "../Leads/LeadsTable";
import LeadsMobileStatusChip from "./LeadsMobileStatusChip";
import { getMobileScoreMeta } from "./mobileScoreMeta";
import { colors, shadows, typography, spacing } from "../../../../assets/styles/tokens";

// Same shadow-token flattening used elsewhere (e.g. KeywordsSearchItemsCardTwo)
// — a shadow token's $value is a list of layers, each turned into one
// "offsetX offsetY blur spread color" CSS shadow, joined for boxShadow.
function buildShadow(shadowToken: any): string {
  return shadowToken.$value
    .map((layer: any) => `${layer.offsetX} ${layer.offsetY} ${layer.blur} ${layer.spread} ${layer.color}`)
    .join(", ");
}

interface LeadMobileCardProps {
  lead: Lead;
  onOpenDetail: () => void;
  onOpenBrandMatch: () => void;
  onViewSharedIp: () => void;
}

function MetricRow({
  label,
  value,
  divider = true,
  paddingBottom = true,
}: {
  label: string;
  value: React.ReactNode;
  divider?: boolean;
  paddingBottom?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        pt: spacing.Spacing.xs.$value,
        pb: paddingBottom ? 0.5 : 0,
        borderTop: divider ? `1px solid ${colors.GrayLightMode[200].$value}` : "none",
      }}
    >
      <Typography
        sx={{
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Medium.$value,
          fontSize: typography.Size.Text.xs.$value,
          lineHeight: typography.Line_height.text.xs.$value,
          letterSpacing: "0%",
          verticalAlign: "middle",
          color: colors.GrayLightMode[700].$value,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ textAlign: "right" }}>{value}</Box>
    </Box>
  );
}

function ScoreGroup({ label, score }: { label: string; score: number | null }) {
  const scoreColor = score === null ? null : getMobileScoreMeta(score).color;
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing.Spacing.md.$value, flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "10px",
          lineHeight: "14px",
          letterSpacing: "0%",
          verticalAlign: "middle",
          color: colors.GrayLightMode[500].$value,
        }}
      >
        {label}
      </Typography>
      <Typography component="span" sx={{ whiteSpace: "nowrap", lineHeight: "20px", display: "flex", alignItems: "center", gap: spacing.Spacing.xxs.$value }}>
        {score === null ? (
          <Box
            component="span"
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              letterSpacing: "0%",
              verticalAlign: "middle",
              color: colors.GrayLightMode[400].$value,
            }}
          >
            —
          </Box>
        ) : (
          <>
            <Box
              component="span"
              sx={{
                fontFamily: typography.Font_Family.$value,
                fontWeight: typography.Font_weight.Semibold.$value,
                fontSize: typography.Size.Text.sm.$value,
                lineHeight: typography.Line_height.text.sm.$value,
                letterSpacing: "0%",
                verticalAlign: "middle",
                color: scoreColor!,
              }}
            >
              {Number.isInteger(score) ? score : score.toFixed(1)}
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "10px",
                lineHeight: "14px",
                letterSpacing: "0%",
                verticalAlign: "middle",
                color: colors.GrayLightMode[900].$value,
              }}
            >
              /10
            </Box>
          </>
        )}
      </Typography>
    </Box>
  );
}

export default function LeadMobileCard({ lead, onOpenDetail, onOpenBrandMatch, onViewSharedIp }: LeadMobileCardProps) {
  const [expanded, setExpanded] = useState(false);
  const statusMeta = STATUS_META[lead.status];
  const qualifiedMeta = getQualifiedMeta(lead.qualifiedByBrand);

  return (
    <Card
      sx={{
        mt: spacing.Spacing.xl.$value,
        borderRadius: spacing.Radius.md.$value,
        border: `1px solid ${colors.GrayLightMode[200].$value}`,
        boxShadow: buildShadow(shadows.Shadows.sm),
      }}
    >
      <CardContent sx={{ p: spacing.Spacing.md.$value, "&:last-child": { pb: spacing.Spacing.md.$value } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Typography
          sx={{
            fontFamily: typography.Font_Family.$value,
            fontWeight: typography.Font_weight.Semibold.$value,
            fontSize: typography.Size.Text.sm.$value,
            lineHeight: typography.Line_height.text.sm.$value,
            letterSpacing: "0%",
            verticalAlign: "middle",
            color: colors.GrayLightMode[900].$value,
            textTransform: "capitalize",
          }}
        >
          {lead.name}{" "}
          <Typography
            component="span"
            sx={{
              fontFamily: typography.Font_Family.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              letterSpacing: "0%",
              verticalAlign: "middle",
              color: colors.GrayLightMode[500].$value,
            }}
          >
            ({lead.id})
          </Typography>
        </Typography>
        <IconButton size="small" onClick={onOpenDetail} sx={{padding: 0 }}>
          <InfoOutlinedIcon fontSize="small" sx={{ color: colors.GrayLightMode[500].$value }} />
        </IconButton>
      </Box>
      <Typography
        sx={{
          fontFamily: typography.Font_Family.$value,
          fontWeight: typography.Font_weight.Regular.$value,
          fontSize: typography.Size.Text.xs.$value,
          lineHeight: typography.Line_height.text.xs.$value,
          letterSpacing: "0%",
          verticalAlign: "middle",
          color: colors.GrayLightMode[400].$value,
        }}
      >
        {lead.email}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: spacing.Spacing.md.$value, mt: spacing.Spacing.md1.$value }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.GrayLightMode[900].$value, flex: 1, minWidth: 0 }}>
          {lead.brand}
        </Typography>
        <LeadsMobileStatusChip label={statusMeta.label} />
        <LeadsMobileStatusChip label={qualifiedMeta.label} />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: spacing.Spacing.lg.$value, mt: spacing.Spacing.lg.$value, justifyContent: "flex-start"}}>
        <ScoreGroup label="LEAD SCORE" score={lead.leadScore} />
        <Divider orientation="vertical" flexItem sx={{ borderColor: colors.GrayLightMode[200].$value }} />
        <ScoreGroup label="APPROVAL LIKELIHOOD" score={lead.approvalLikelihood} />
      </Box>

      <Box
        onClick={() => setExpanded((current) => !current)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: spacing.Spacing.xxs.$value,
          mt: spacing.Spacing.md.$value,
          cursor: "pointer",
          color: colors.Brand[700].$value,
        }}
      >
        <Typography
          sx={{
            fontFamily: typography.Font_Family.$value,
            fontWeight: typography.Font_weight.Regular.$value,
            fontSize: typography.Size.Text.xs.$value,
            lineHeight: typography.Line_height.text.xs.$value,
            letterSpacing: "0%",
            verticalAlign: "middle",
          }}
        >
          {expanded ? "Show Less Metrics" : "Show More Metrics"}
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        />
      </Box>

      {expanded && (
        <Box sx={{ mt: spacing.Spacing.md.$value }}>
          <MetricRow
            label="INCOME"
            divider={false}
            value={
              <Typography
                sx={{
                  fontFamily: typography.Font_Family.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  fontSize: typography.Size.Text.sm.$value,
                  lineHeight: typography.Line_height.text.sm.$value,
                  color: colors.GrayLightMode[900].$value,
                }}
              >
                {formatCurrency(lead.incomeFromBrand)}
              </Typography>
            }
          />
          <MetricRow
            label="ACQUIRED"
            value={
              <>
                <Typography
                  sx={{
                    fontFamily: typography.Font_Family.$value,
                    fontWeight: typography.Font_weight.Semibold.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    lineHeight: typography.Line_height.text.sm.$value,
                    color: colors.GrayLightMode[900].$value,
                  }}
                >
                  {formatAcquiredDate(lead.date)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: colors.GrayLightMode[500].$value }}>
                  {formatAcquiredTime(lead.date)}
                </Typography>
              </>
            }
          />
          <MetricRow
            label="IP ADDRESS"
            value={
              <>
                <Typography
                  sx={{
                    fontFamily: typography.Font_Family.$value,
                    fontWeight: typography.Font_weight.Semibold.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    lineHeight: typography.Line_height.text.sm.$value,
                    color: colors.GrayLightMode[900].$value,
                  }}
                >
                  {lead.ipAddress ?? "—"}
                </Typography>
                {lead.duplicateIpLeads.length > 0 && (
                  <Typography
                    onClick={onViewSharedIp}
                    sx={{
                      fontFamily: typography.Font_Family.$value,
                      fontWeight: typography.Font_weight.Regular.$value,
                      fontSize: typography.Size.Text.xs.$value,
                      lineHeight: typography.Line_height.text.xs.$value,
                      color: colors.Brand[600].$value,
                      cursor: "pointer",
                    }}
                  >
                    {lead.duplicateIpLeads.length} shared:{" "}
                    {lead.duplicateIpLeads.slice(0, 2).map((dup) => `#${dup.id}`).join(", ")}
                    {lead.duplicateIpLeads.length > 2 ? ` +${lead.duplicateIpLeads.length - 2}` : ""}
                  </Typography>
                )}
              </>
            }
          />
          <MetricRow
            label="BRAND MATCHES"
            paddingBottom={false}
            value={
              <Typography
                component="span"
                onClick={onOpenBrandMatch}
                sx={{
                  fontFamily: typography.Font_Family.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  fontSize: typography.Size.Text.sm.$value,
                  lineHeight: typography.Line_height.text.sm.$value,
                  color: colors.Brand[700].$value,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                Check Matches
              </Typography>
            }
          />
        </Box>
      )}
      </CardContent>
    </Card>
  );
}
